import React, { useEffect, useRef, useState } from 'react';

import { Modal } from '@/components';
import { Alert } from '@/design-system/alert';
import { Button } from '@/design-system/button';
import { toast } from '@/design-system/toaster';

import { parseWarehouseError, warehouseApi } from '../../api/warehouse.api';
import { CsvErrorTable, CsvFileProblem } from '../../components/CsvErrorTable';
import {
  describeUploadError,
  localFileProblem,
  messagesToErrors,
  type PickError,
} from '../../components/csvImportErrors';
import { DisabledReason } from '../../components/dialogs';
import { metaCls, SumTile } from '../../components/ui';
import { csvFileError, downloadBlob } from '../../format';
import type { CapacityImportResult } from '../../types';

/** Backend limit on a capacity sheet. */
const MAX_BYTES = 5 * 1024 * 1024;

/** CSV column → label shown next to the raw column name in the error table. */
export const CAPACITY_CSV_COLUMN_LABEL: Readonly<Record<string, string>> = {
  row: 'Seluruh baris',
  path: 'Path bin',
  name: 'Nama bin',
  capacity_cbm: 'Kapasitas (m³)',
  capacity_kg: 'Kapasitas (kg)',
  capacity_qty: 'Kapasitas (unit)',
  width_cm: 'Lebar (cm)',
  depth_cm: 'Kedalaman (cm)',
  height_cm: 'Tinggi (cm)',
};

const columnLabel = (field: string): string | null =>
  CAPACITY_CSV_COLUMN_LABEL[field] ?? null;

const FILE_HINT =
  'Download template, salin data ke sana (nama kolom di baris pertama harus sama persis), lalu pilih ulang file.';

/**
 * "Import CSV kapasitas": pick a CSV (one row per BIN: path or name +
 * capacity_cbm / kg / qty + width / depth / height cm; a rack / location row is
 * rejected, capacity is per bin only) → upload → the server validates every row first
 * and saves all or nothing. A sheet with a wrong row saves nothing; its
 * messages (one per line) are listed so the file can be fixed and uploaded again.
 */
export const CapacityImportModal: React.FC<{
  open: boolean;
  hubID: number;
  onClose: () => void;
  /** At least one row was saved (the list should reload). */
  onImported: (result: CapacityImportResult) => void;
}> = ({ open, hubID, onClose, onImported }) => {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<CapacityImportResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState<PickError | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    setFile(null);
    setResult(null);
    setBusy(false);
    setDownloading(false);
    setError(null);
  }, [open]);

  if (!open) return null;

  const pick = (f: File | null) => {
    setResult(null);
    setError(null);
    setFile(null);
    if (!f) return;
    const problem = csvFileError(f, MAX_BYTES);
    if (problem) {
      setError(
        localFileProblem(
          problem,
          'Pakai file .csv dari "Download template", maksimal 5 MB.'
        )
      );
      return;
    }
    setFile(f);
  };

  const upload = async () => {
    if (!file || busy) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await warehouseApi.importStorageCapacity(hubID, file);
      setResult(res);
      if (res.totalFail === 0 && res.totalSuccess > 0) {
        toast.success(`Kapasitas ${res.totalSuccess} baris tersimpan`);
        onImported(res);
      }
    } catch (err) {
      setError(
        describeUploadError(parseWarehouseError(err), {
          fileHint: FILE_HINT,
          requestTitle: 'Import gagal',
          requestAction: 'Import kapasitas',
        })
      );
    } finally {
      setBusy(false);
    }
  };

  const template = async () => {
    setDownloading(true);
    try {
      const { blob, filename } =
        await warehouseApi.downloadStorageCapacityCsv(hubID);
      downloadBlob(blob, filename);
    } catch (err) {
      toast.error('Template gagal di-download', {
        description: parseWarehouseError(err).message,
      });
    } finally {
      setDownloading(false);
    }
  };

  // Structured errors; an older server only sends the "Baris N: …" strings.
  const rowErrors = !result
    ? []
    : (result.errors ??
      (result.totalFail > 0 ? messagesToErrors(result.messages) : []));
  const saved = !!result && result.totalFail === 0 && result.totalSuccess > 0;
  const blocked = busy
    ? 'Mengunggah file…'
    : saved
      ? null
      : !file
        ? 'Pilih file CSV'
        : null;

  return (
    <Modal
      isOpen={open}
      onClose={onClose}
      icon="ri-upload-2-line"
      title="Import CSV kapasitas"
      width="lg"
      closeOnBackdropClick={!busy}
      closeOnEscape={!busy}
      footer={
        <div className="flex items-center justify-end gap-2">
          {blocked && !busy && (
            <DisabledReason testId="capimport-reason" className="mr-auto">
              {blocked}
            </DisabledReason>
          )}
          <Button
            tone="neutral"
            style="stroke"
            onClick={onClose}
            disabled={busy}
          >
            {saved ? 'Tutup' : 'Batal'}
          </Button>
          {!saved && (
            <Button
              onClick={upload}
              disabled={!file}
              loading={busy}
              data-testid="capimport-submit"
            >
              Import kapasitas
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-testid="capimport">
        <p className="text-sm text-text-sub-600">
          Satu baris = satu bin. Kolom: <code>path</code> bin (nama dipisah
          &quot;/&quot;, mis. Gudang Utama/Rak A/A1) atau <code>name</code>{' '}
          (nama bin, unik di hub), capacity_cbm, capacity_kg, capacity_qty,
          width_cm, depth_cm, height_cm. Sel kosong = tidak diubah. Baris rak /
          lokasi ditolak: kapasitas hanya diatur per bin. Maks. 5 MB / 5.000
          baris.
        </p>
        <Alert
          status="information"
          appearance="stroke"
          size="sm"
          data-testid="capimport-atomic"
        >
          Import bersifat atomik: semua baris diperiksa dulu, dan kalau satu
          baris saja salah, tidak ada baris yang disimpan. Perbaiki file sesuai
          pesan di bawah lalu unggah ulang.
        </Alert>
        <div className="flex items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept=".csv,text/csv"
            className="hidden"
            data-testid="capimport-file"
            aria-label="File CSV kapasitas"
            onChange={(e) => {
              pick(e.target.files?.[0] ?? null);
              e.target.value = '';
            }}
          />
          <Button
            tone="neutral"
            style="stroke"
            disabled={busy}
            onClick={() => inputRef.current?.click()}
            leftIcon={<i className="ri-file-upload-line text-[14px]" />}
          >
            {file ? 'Ganti file' : 'Pilih file CSV'}
          </Button>
          {file && (
            <span className={metaCls} data-testid="capimport-filename">
              {file.name}
            </span>
          )}
          <span className="flex-1" />
          <Button
            tone="primary"
            style="link"
            size="xs"
            className="text-xs font-semibold"
            loading={downloading}
            data-testid="capimport-template"
            onClick={template}
            leftIcon={
              <i className="ri-download-2-line text-[14px]" aria-hidden />
            }
          >
            Download template
          </Button>
        </div>
        {error && <CsvFileProblem problem={error} testId="capimport-error" />}
        {result && (
          <>
            <div className="grid grid-cols-3 gap-3">
              <SumTile
                label="Total baris"
                value={result.totalData}
                tone="neutral"
                testId="capimport-total"
              />
              <SumTile
                label="Tersimpan"
                value={result.totalSuccess}
                tone="success"
                testId="capimport-success"
              />
              <SumTile
                label="Gagal"
                value={result.totalFail}
                tone={result.totalFail ? 'error' : 'neutral'}
                testId="capimport-fail"
              />
            </div>
            {rowErrors.length > 0 && (
              <div data-testid="capimport-messages">
                <CsvErrorTable
                  errors={rowErrors}
                  columnLabel={columnLabel}
                  fileName={file?.name}
                />
                <p className={`${metaCls} mt-2`}>
                  Tidak ada baris yang disimpan — perbaiki file lalu pilih
                  ulang.
                </p>
              </div>
            )}
            {rowErrors.length === 0 && result.messages.length > 0 && (
              <Alert
                status={result.totalFail > 0 ? 'error' : 'information'}
                appearance="stroke"
                size="lg"
                title={
                  result.totalFail > 0
                    ? 'Tidak ada baris yang disimpan — perbaiki baris berikut'
                    : 'Catatan'
                }
                data-testid="capimport-messages"
              >
                <ul className="max-h-[220px] list-disc overflow-auto pl-4">
                  {result.messages.map((m, i) => (
                    <li key={`${i}-${m}`}>{m}</li>
                  ))}
                </ul>
              </Alert>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};
